import sv from "./locales/sv.json";
import en from "./locales/en.json";
import { DEFAULT_LOCALE, type Locale } from "./supported";

/**
 * Translation dictionary type. The Swedish bundle is the source of truth;
 * other locales are partial overrides keyed by the same flat key strings.
 */
export type TranslationDict = Record<string, string>;

const BUNDLES: Record<Locale, TranslationDict> = {
  sv: sv as TranslationDict,
  en: en as TranslationDict,
};

/**
 * Returns the loaded translation bundle for a locale.
 * Exposed for testing — runtime code should use `translate()` or `useTranslate()`.
 */
export function getBundle(locale: Locale): TranslationDict {
  return BUNDLES[locale] ?? BUNDLES[DEFAULT_LOCALE];
}

/**
 * Substitutes `{varName}` placeholders in a translated string with values from `vars`.
 * Unknown placeholders are left intact so they are noticeable in QA.
 */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    if (Object.prototype.hasOwnProperty.call(vars, key)) {
      return String(vars[key]);
    }
    return match;
  });
}

/**
 * Translate a key into the requested locale, falling back to the Swedish
 * source string when a key is missing in the target bundle.
 *
 * Fallback policy:
 *   1. `locale` bundle has the key → use it.
 *   2. Swedish (`sv`) bundle has the key → use it.
 *   3. Otherwise return the key itself (developer-visible fallback).
 *
 * NEVER return an empty string. The key fallback exists so missing keys
 * are caught in QA rather than rendered as blank UI.
 */
export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const localized = BUNDLES[locale]?.[key];
  if (typeof localized === "string") {
    return interpolate(localized, vars);
  }

  const source = BUNDLES[DEFAULT_LOCALE]?.[key];
  if (typeof source === "string") {
    return interpolate(source, vars);
  }

  return key;
}
